import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import nurseryService from '../../services/nursery';
import orchardService from '../../services/orchard';
import type { NurseryLocation, PlaceFilter } from '../../types/nursery';
import { compareBlocks, rowLabel } from '../../utils/nurseryRowSlots';

interface Opt { id: number; name: string; sort_order?: number | null }

interface Props {
  value: PlaceFilter;
  onChange: (next: PlaceFilter) => void;
  /** Wording of the empty farm option: a filter says "All farms", a picker "Any farm". */
  allLabel?: 'All' | 'Any';
}

const SELECT = 'w-full sm:w-auto sm:max-w-xs border border-neutral-300 rounded-lg px-3 py-2 text-sm disabled:bg-neutral-50 disabled:text-neutral-400';

/** One option per row of the block, and per side of the track where it has one. */
function rowOptions(locations: NurseryLocation[]) {
  const seen = new Map<string, { value: string; label: string; row: number; side: string }>();
  for (const l of locations) {
    if (l.location_type !== 'nursery_row' || l.row_number == null) continue;
    const value = `${l.row_number}:${l.row_side ?? 'none'}`;
    if (!seen.has(value)) seen.set(value, { value, label: rowLabel(l.row_number, l.row_side), row: l.row_number, side: l.row_side ?? '' });
  }
  return [...seen.values()].sort((a, b) => a.row - b.row || a.side.localeCompare(b.side));
}

/**
 * Narrows to where stock stands: a farm, then a block in it, then a row in
 * that (and its side of the track). Each step clears the ones below it.
 */
export default function LotPlaceFilter({ value, onChange, allLabel = 'All' }: Props) {
  const { t } = useTranslation('nursery', { keyPrefix: 'LotPlaceFilter' });
  const any = allLabel === 'Any';
  const [farms, setFarms] = useState<Opt[]>([]);
  const [blocks, setBlocks] = useState<Opt[]>([]);
  const [locations, setLocations] = useState<NurseryLocation[]>([]);

  useEffect(() => {
    orchardService.getFarms().then(res => {
      if (res.success) setFarms(((res.data as { farms?: Opt[] })?.farms || (res.data as unknown as Opt[]) || []));
    }).catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!value.farm_id) { setBlocks([]); return; }
    orchardService.getBlocks(value.farm_id).then(res => {
      if (res.success) setBlocks([...((res.data as { blocks?: Opt[] })?.blocks || (res.data as Opt[]) || [])].sort(compareBlocks));
    }).catch(() => undefined);
  }, [value.farm_id]);

  useEffect(() => {
    if (!value.block_id) { setLocations([]); return; }
    nurseryService.getLocations({ block_id: value.block_id }).then(res => {
      if (res.success) setLocations(res.data || []);
    }).catch(() => undefined);
  }, [value.block_id]);

  const rows = useMemo(() => rowOptions(locations), [locations]);
  const rowValue = value.row_number ? `${value.row_number}:${value.row_side ?? 'none'}` : '';

  return (
    <>
      <select aria-label={t('farm')} value={value.farm_id ?? ''} className={SELECT}
        onChange={e => onChange({ farm_id: e.target.value ? Number(e.target.value) : undefined })}>
        <option value="">{any ? t('anyFarms') : t('allFarms')}</option>
        {farms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
      </select>
      <select aria-label={t('block')} value={value.block_id ?? ''} disabled={!value.farm_id} className={SELECT}
        onChange={e => onChange({ farm_id: value.farm_id, block_id: e.target.value ? Number(e.target.value) : undefined })}>
        <option value="">{any ? t('anyBlocks') : t('allBlocks')}</option>
        {blocks.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
      </select>
      <select aria-label={t('row')} value={rowValue} disabled={!value.block_id || rows.length === 0} className={SELECT}
        onChange={e => {
          const [row, side] = e.target.value.split(':');
          onChange({
            farm_id: value.farm_id,
            block_id: value.block_id,
            ...(row ? { row_number: Number(row), row_side: side as PlaceFilter['row_side'] } : {}),
          });
        }}>
        <option value="">{any ? t('anyRows') : t('allRows')}</option>
        {rows.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
      </select>
    </>
  );
}
